<template>
  <!-- v-model не используется, потому что на мобилках почему-то при вводе букв не происходит изменение search value -->
  <div class="base-search">
    <input
      ref="input"
      :value="searchValue"
      class="base-search__input"
      :style="inputStyles"
      placeholder="Поиск по сайту"
      @input="setSearchValue"
      @keyup.enter="goSearch()"
    />
    <BaseButton
      v-if="!searchValue"
      class="base-search__btn base-search__btn--search"
      aria-label="Поиск"
    >
      <SvgAsset
        class="base-search__icon"
        :content="searchPath"
      />
    </BaseButton>
    <BaseButton
      v-else
      class="base-search__btn base-search__btn--reset"
      aria-label="Отчистить поле поиска"
      @click="resetValue"
    >
      <SvgAsset
        class="base-search__icon"
        :content="crossMobilePath"
      />
    </BaseButton>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import BaseButton from '~/components/BaseComponents/BaseButton.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import crossMobilePath from '@/assets/images/svg/cross--mobile.svg?raw';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import searchPath from '@/assets/images/svg/search.svg?raw';

  const THEMES = {
    mobile: {
      color: '#C722CA',
      radius: '8px',
    },
    header: {
      color: '#E7E7E7',
      radius: '8px',
    },
    archive: {
      color: '#E7E7E7',
      radius: '4px',
    },
  } as Record<string, Record<string, string>>;

  export default defineNuxtComponent({
    name: 'BaseSearch',
    components: { BaseButton, SvgAsset },
    props: {
      theme: {
        type: String as PropType<string>,
        required: true,
      },

      syncFromQuery: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      autoFocus: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },

    data() {
      return {
        searchValue: (this.syncFromQuery ? this.$route.query.s || '' : '') as string,

        firstEnter: true,
      };
    },

    computed: {
      searchPath: () => searchPath,
      crossMobilePath: () => crossMobilePath,
      inputStyles(): Record<string, string> {
        return {
          '--border-color-input': THEMES[this.theme].color,
          '--border-radius-input': THEMES[this.theme].radius,
        };
      },

      currentSlug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },
    },

    watch: {
      '$route.query.s'(query: string) {
        if (this.syncFromQuery) {
          this.searchValue = query;
          this.$emit('search', query);
        }
      },
    },

    mounted() {
      if (this.autoFocus) {
        this.inputFocus();
      }
    },

    methods: {
      setSearchValue(e: InputEvent) {
        if (this.firstEnter) {
          this.$sendYandexMetrika({
            level1: 'Событие_Поиск',
            level6: 'Ввод запроса',
          });
        }
        this.firstEnter = false;
        // @ts-expect-error event target typing is not inferred here
        this.searchValue = e?.target?.value?.trim();
      },

      resetValue(): void {
        this.searchValue = '';
        this.inputFocus();

        this.$sendYandexMetrika({
          level1: 'Событие_Поиск',
          level6: 'Очистка запроса',
        });
      },

      inputFocus(): void {
        const input = this.$refs.input as HTMLElement;
        input.focus();
      },

      goSearch() {
        if (!this.searchValue) return;

        this.$emit('search', this.searchValue);

        this.$sendYandexMetrika({
          level1: 'Событие_Поиск',
          level6: 'Отправка запроса',
        });

        if (this.$route.name === 'search') {
          this.$router?.replace({
            path: '/search/',
            query: { s: this.searchValue },
          });
        } else {
          location.href = `/search/?s=${this.searchValue}`;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .base-search {
    @include normalText;
    position: relative;

    &__btn {
      height: 24px;
      width: 24px;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 15px;

      &--search {
        pointer-events: none;
      }
    }

    &__input {
      font-family: $text-font--1;
      font-size: 16px;
      font-weight: 400;
      display: block;
      border: 1px solid var(--border-color-input);
      border-radius: var(--border-radius-input);
      width: 100%;
      height: var(--search-input-height, 40px);
      padding: $spacing-2 $spacing-10 $spacing-2 $spacing-4;

      &:focus-visible {
        border: 1px solid $hover-color--1;
      }
    }

    &__icon {
      :deep() {
        svg path {
          fill: $icon-color--1;
        }
      }

      @include hover {
        :deep() {
          svg path {
            fill: $hover-color--4;
          }
        }
      }
    }
  }
</style>
